import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useGame } from '../../context/GameContext';
import { resources } from '../../data';
import { DUNGEON_RESOURCES } from '../../data/dungeon';

// ============================================================
// ANIMATED VALUE HOOK - Smooth number transitions (200ms)
// ============================================================
function useAnimatedValue(targetValue, duration = 200) {
  const [displayValue, setDisplayValue] = useState(targetValue);
  const animationRef = useRef(null);
  const startValueRef = useRef(targetValue);
  const startTimeRef = useRef(null);

  useEffect(() => {
    // Cancel any existing animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    startValueRef.current = displayValue;
    startTimeRef.current = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic for snappy feel
      const eased = 1 - Math.pow(1 - progress, 3);
      const newValue = startValueRef.current + (targetValue - startValueRef.current) * eased;
      setDisplayValue(newValue);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [targetValue, duration]);

  return displayValue;
}

// ============================================================
// NUMBER FORMATTING - Handles up to Trillions
// ============================================================
function formatNumber(num) {
  if (num === undefined || num === null) return '0';
  if (num >= 1e12) return (num / 1e12).toFixed(2) + 'T';
  if (num >= 1e9) return (num / 1e9).toFixed(2) + 'B';
  if (num >= 1e6) return (num / 1e6).toFixed(2) + 'M';
  if (num >= 1e4) return (num / 1e3).toFixed(1) + 'K';
  if (num >= 1e3) return (num / 1e3).toFixed(2) + 'K';
  return Math.floor(num).toLocaleString();
}

function formatNumberCompact(num) {
  if (num === undefined || num === null) return '0';
  if (num >= 1e12) return (num / 1e12).toFixed(1) + 'T';
  if (num >= 1e9) return (num / 1e9).toFixed(1) + 'B';
  if (num >= 1e6) return (num / 1e6).toFixed(1) + 'M';
  if (num >= 1e3) return (num / 1e3).toFixed(1) + 'K';
  return Math.floor(num).toString();
}

function formatRate(rate) {
  if (rate === undefined || rate === null || rate === 0) return null;
  const prefix = rate > 0 ? '+' : '';
  if (Math.abs(rate) >= 1000) return prefix + formatNumberCompact(rate);
  if (Math.abs(rate) >= 100) return prefix + Math.floor(rate);
  if (Math.abs(rate) >= 10) return prefix + rate.toFixed(1);
  if (Math.abs(rate) >= 1) return prefix + rate.toFixed(2);
  return prefix + rate.toFixed(3);
}

function formatRateDetailed(rate) {
  if (rate === undefined || rate === null) return '0';
  if (Math.abs(rate) >= 1000) return formatNumber(rate);
  if (Math.abs(rate) >= 100) return Math.floor(rate).toString();
  if (Math.abs(rate) >= 10) return rate.toFixed(1);
  if (Math.abs(rate) >= 1) return rate.toFixed(2);
  return rate.toFixed(3);
}

// Format gain for floating text
function formatGain(num) {
  if (num >= 1e6) return '+' + (num / 1e6).toFixed(1) + 'M';
  if (num >= 1e3) return '+' + (num / 1e3).toFixed(1) + 'K';
  if (num >= 100) return '+' + Math.floor(num);
  if (num >= 1) return '+' + num.toFixed(1);
  return '+' + num.toFixed(2);
}

// ============================================================
// MILESTONE CALCULATIONS
// ============================================================
function getNextMilestone(current) {
  const milestones = [100, 500, 1000, 5000, 10000, 50000, 100000, 500000, 1e6, 5e6, 1e7, 5e7, 1e8, 5e8, 1e9, 5e9, 1e10, 5e10, 1e11, 5e11, 1e12];
  for (const m of milestones) {
    if (current < m) return m;
  }
  return current * 10;
}

function getMilestoneProgress(current) {
  const next = getNextMilestone(current);
  const prev = getPrevMilestone(current);
  if (next === prev) return 100;
  return Math.min(100, ((current - prev) / (next - prev)) * 100);
}

function getPrevMilestone(current) {
  const milestones = [0, 100, 500, 1000, 5000, 10000, 50000, 100000, 500000, 1e6, 5e6, 1e7, 5e7, 1e8, 5e8, 1e9, 5e9, 1e10, 5e10, 1e11, 5e11, 1e12];
  for (let i = milestones.length - 1; i >= 0; i--) {
    if (current >= milestones[i]) return milestones[i];
  }
  return 0;
}

// ============================================================
// MODIFIER TOOLTIP HELPER
// ============================================================
function getModifierTooltip(breakdown) {
  if (!breakdown) return 'Has active modifiers';
  
  const parts = [];
  
  if (breakdown.advisorBonus > 0) {
    parts.push(`👑 Advisor: +${Math.round(breakdown.advisorBonus * 100)}%`);
  }
  if (breakdown.advisorPenalty < 0) {
    parts.push(`👑 Penalty: ${Math.round(breakdown.advisorPenalty * 100)}%`);
  }
  if (breakdown.wonderBonus > 0) {
    parts.push(`🏛️ Wonder: +${Math.round(breakdown.wonderBonus * 100)}%`);
  }

  return parts.length > 0 ? parts.join('\n') : 'Has active modifiers';
}

// ============================================================
// TIER CONFIGURATION
// ============================================================
const RESOURCE_TIERS = {
  basic: {
    name: 'Basic',
    icon: '🏠',
    resources: ['wood', 'stone', 'food'],
  },
  economy: {
    name: 'Economy',
    icon: '💰',
    resources: ['gold', 'iron', 'population'],
  },
  knowledge: {
    name: 'Knowledge',
    icon: '📚',
    resources: ['knowledge', 'culture'],
  },
  advanced: {
    name: 'Advanced',
    icon: '⚒️',
    resources: ['steel', 'coal', 'gems'],
  },
  industrial: {
    name: 'Industrial',
    icon: '🔧',
    resources: ['tools', 'machinery'],
  },
  science: {
    name: 'Science',
    icon: '🔬',
    resources: ['science', 'energy'],
  },
  magic: {
    name: 'Arcane',
    icon: '✨',
    resources: ['magic', 'artifacts', 'crystals'],
  },
};

// ============================================================
// DUNGEON RESOURCE TIERS
// ============================================================
const DUNGEON_RESOURCE_TIERS = {
  common: {
    name: 'Common',
    icon: '⚪',
    resources: ['bone', 'leather', 'cloth', 'ironOre'],
  },
  uncommon: {
    name: 'Uncommon',
    icon: '🟢',
    resources: ['essence', 'crystal', 'rune'],
  },
  rare: {
    name: 'Rare',
    icon: '🔵',
    resources: ['soulShard', 'dragonScale', 'voidFragment'],
  },
  epic: {
    name: 'Epic',
    icon: '🟣',
    resources: ['divineEssence', 'eldritchHeart'],
  },
  legendary: {
    name: 'Legendary',
    icon: '🟡',
    resources: ['cosmicOre', 'eterniumIngot'],
  },
};

// ============================================================
// MAIN COMPONENT
// ============================================================
function ResourcePanel({ onNotification, showTooltip, hideTooltip }) {
  const {
    playerResources,
    getProductionRate,
    getProductionBreakdown,
    playerCharacter,
  } = useGame();

  // === RESOURCE TYPE TOGGLE ===
  const [resourceType, setResourceType] = useState('kingdom'); // 'kingdom' or 'dungeon'

  // === EXPANDED RESOURCES (Set for multiple) ===
  const [expandedResources, setExpandedResources] = useState(new Set());

  // === SEARCH ===
  const [searchTerm, setSearchTerm] = useState('');

  // === FILTER: Active only (has production) ===
  const [activeOnly, setActiveOnly] = useState(false);

  // === CLICK TO EXPAND/CONTRACT (toggle in set) ===
  const handleResourceClick = useCallback((resourceId) => {
    setExpandedResources(prev => {
      const next = new Set(prev);
      if (next.has(resourceId)) {
        next.delete(resourceId);
      } else {
        next.add(resourceId);
      }
      return next;
    });
  }, []);
  
  // === COLLAPSE ALL ===
  const collapseAll = useCallback(() => {
    setExpandedResources(new Set());
  }, []);

  return (
    <div className="resources-panel">
      {/* Header */}
      <div className="rp-header">
        <div className="rp-header-top">
          <h3>📦 Resources</h3>
          <div className="rp-header-actions">
            <button
              className={`rp-action-btn ${activeOnly ? 'active' : ''}`}
              onClick={() => setActiveOnly(!activeOnly)}
              title="Show only producing resources"
            >
              {activeOnly ? '● Active' : '○ All'}
            </button>
            {expandedResources.size > 0 && (
              <button
                className="rp-action-btn"
                onClick={collapseAll}
                title="Collapse all"
              >
                ▲
              </button>
            )}
          </div>
        </div>

        {/* Resource Type Toggle */}
        <div className="rp-type-toggle">
          <button
            className={`rp-type-btn ${resourceType === 'kingdom' ? 'active' : ''}`}
            onClick={() => setResourceType('kingdom')}
          >
            🏰 Kingdom
          </button>
          <button
            className={`rp-type-btn ${resourceType === 'dungeon' ? 'active' : ''}`}
            onClick={() => setResourceType('dungeon')}
          >
            ⚔️ Dungeon
          </button>
        </div>

        {/* Search Bar */}
        <div className="rp-search-container">
          <span className="rp-search-icon">🔍</span>
          <input
            type="text"
            className="rp-search-input"
            placeholder="Search resources..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              className="rp-search-clear"
              onClick={() => setSearchTerm('')}
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Resources List */}
      <div className="rp-resources-list">
        {resourceType === 'kingdom' ? (
          /* KINGDOM RESOURCES */
          (() => {
            let hasAnyVisible = false;

            const tierElements = Object.entries(RESOURCE_TIERS).map(([tierId, tier]) => {
              // Filter resources
              const visibleResources = tier.resources.filter(id => {
                if (!id || !resources[id]) return false;

                const hasAmount = (playerResources[id] || 0) > 0;
                const rate = getProductionRate(id);
                const hasProduction = rate > 0;

                // Must have amount or production to show
                if (!hasAmount && !hasProduction) return false;

                // Active only filter
                if (activeOnly && rate === 0) return false;

                // Search filter
                if (searchTerm) {
                  const term = searchTerm.toLowerCase();
                  const res = resources[id];
                  const matchName = res.name.toLowerCase().includes(term);
                  const matchId = id.toLowerCase().includes(term);
                  if (!matchName && !matchId) return false;
                }

                return true;
              });

              if (visibleResources.length === 0) return null;
              hasAnyVisible = true;

              return (
                <div key={tierId} className="rp-tier-group">
                  {/* Tier Header */}
                  <div className="rp-tier-header">
                    <span className="rp-tier-icon">{tier.icon}</span>
                    <span className="rp-tier-name">{tier.name}</span>
                    <span className="rp-tier-count">{visibleResources.length}</span>
                  </div>

                  {/* Resource Rows */}
                  <div className="rp-tier-content">
                    {visibleResources.map(id => {
                      const res = resources[id];
                      const amt = playerResources[id] || 0;
                      const breakdown = getProductionBreakdown ? getProductionBreakdown(id) : null;
                      const rate = breakdown?.final || getProductionRate(id);
                      const hasModifiers = breakdown && (
                        breakdown.advisorBonus !== 0 ||
                        breakdown.advisorPenalty !== 0 ||
                        breakdown.wonderBonus !== 0
                      );
                      const isExpanded = expandedResources.has(id);
                      const isZeroProduction = rate === 0;

                      return (
                        <ResourceRow
                          key={id}
                          resourceId={id}
                          resource={res}
                          amount={amt}
                          rate={rate}
                          breakdown={breakdown}
                          hasModifiers={hasModifiers}
                          isExpanded={isExpanded}
                          isZeroProduction={isZeroProduction}
                          onClick={() => handleResourceClick(id)}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            });

            // Show empty state if nothing visible
            if (!hasAnyVisible) {
              return (
                <div className="rp-empty-state">
                  <span className="rp-empty-icon">🔍</span>
                  <span className="rp-empty-text">
                    {searchTerm ? `No resources match "${searchTerm}"` : 'No resources yet'}
                  </span>
                </div>
              );
            }

            return tierElements;
          })()
        ) : (
          /* DUNGEON RESOURCES */
          (() => {
            let hasAnyVisible = false;

            const tierElements = Object.entries(DUNGEON_RESOURCE_TIERS).map(([tierId, tier]) => {
              // Filter resources
              const visibleResources = tier.resources.filter(id => {
                if (!id || !DUNGEON_RESOURCES[id]) return false;

                // Search filter
                if (searchTerm) {
                  const term = searchTerm.toLowerCase();
                  const res = DUNGEON_RESOURCES[id];
                  const matchName = res.name.toLowerCase().includes(term);
                  const matchId = id.toLowerCase().includes(term);
                  if (!matchName && !matchId) return false;
                }

                return true;
              });

              if (visibleResources.length === 0) return null;
              hasAnyVisible = true;

              return (
                <div key={tierId} className="rp-tier-group">
                  {/* Tier Header */}
                  <div className="rp-tier-header">
                    <span className="rp-tier-icon">{tier.icon}</span>
                    <span className="rp-tier-name">{tier.name}</span>
                    <span className="rp-tier-count">{visibleResources.length}</span>
                  </div>

                  {/* Resource Rows */}
                  <div className="rp-tier-content">
                    {visibleResources.map(id => {
                      const res = DUNGEON_RESOURCES[id];
                      // Get material count from playerCharacter.inventory
                      const materialStack = playerCharacter?.inventory?.find(item => item.id === id && item.category === 'material');
                      const amt = materialStack?.quantity || 0;
                      const isExpanded = expandedResources.has(id);

                      return (
                        <DungeonResourceRow
                          key={id}
                          resourceId={id}
                          resource={res}
                          amount={amt}
                          isExpanded={isExpanded}
                          onClick={() => handleResourceClick(id)}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            });

            // Show empty state if nothing visible
            if (!hasAnyVisible) {
              return (
                <div className="rp-empty-state">
                  <span className="rp-empty-icon">🔍</span>
                  <span className="rp-empty-text">
                    {searchTerm ? `No dungeon resources match "${searchTerm}"` : 'No resources found'}
                  </span>
                </div>
              );
            }

            return tierElements;
          })()
        )}
      </div>
    </div>
  );
}

// ============================================================
// RESOURCE ROW COMPONENT (with dopamine feedback)
// ============================================================
function ResourceRow({
  resourceId,
  resource,
  amount,
  rate,
  breakdown,
  hasModifiers,
  isExpanded,
  isZeroProduction,
  onClick,
}) {
  // Smooth animated value for the resource amount
  const animatedAmount = useAnimatedValue(amount);

  const milestoneProgress = getMilestoneProgress(amount);
  const nextMilestone = getNextMilestone(amount);

  // === DOPAMINE FEEDBACK STATE ===
  const prevAmountRef = useRef(amount);
  const [isGaining, setIsGaining] = useState(false);
  const [floatingGains, setFloatingGains] = useState([]);
  const gainTimeoutRef = useRef(null);
  const accumulatedGainRef = useRef(0);
  const lastFloatTimeRef = useRef(Date.now());
  const nextFloatIntervalRef = useRef(800 + Math.random() * 400); // 800-1200ms random
  const gainIdRef = useRef(0);
  
  // Track gains and trigger animations
  useEffect(() => {
    const prevAmount = prevAmountRef.current;
    const gain = amount - prevAmount;
    
    if (gain > 0) {
      // Accumulate gains
      accumulatedGainRef.current += gain;
      
      // Trigger gaining animation (icon pulse, amount flash)
      setIsGaining(true);
      
      // Clear previous timeout
      if (gainTimeoutRef.current) {
        clearTimeout(gainTimeoutRef.current);
      }
      
      // Check if enough time has passed to show a floating number
      const now = Date.now();
      const timeSinceLastFloat = now - lastFloatTimeRef.current;
      
      if (timeSinceLastFloat >= nextFloatIntervalRef.current && accumulatedGainRef.current > 0) {
        const gainToShow = accumulatedGainRef.current;
        accumulatedGainRef.current = 0;
        lastFloatTimeRef.current = now;
        
        // Set next random interval (800-1200ms)
        nextFloatIntervalRef.current = 800 + Math.random() * 400;
        
        // Determine size class based on gain significance
        let sizeClass = 'size-sm';
        if (gainToShow >= 1000) sizeClass = 'size-xl';
        else if (gainToShow >= 100) sizeClass = 'size-lg';
        else if (gainToShow >= 10) sizeClass = 'size-md';
        
        // Random X offset for variety
        const randomX = (Math.random() - 0.5) * 16;
        
        // Create floating gain
        const gainId = gainIdRef.current++;
        const newGain = {
          id: gainId,
          value: gainToShow,
          x: randomX,
          sizeClass,
        };
        
        setFloatingGains(prev => [...prev.slice(-2), newGain]); // Keep max 3
        
        // Remove after animation
        setTimeout(() => {
          setFloatingGains(prev => prev.filter(g => g.id !== gainId));
        }, 900);
      }
      
      // Reset gaining state after a short delay
      gainTimeoutRef.current = setTimeout(() => {
        setIsGaining(false);
      }, 200);
    }
    
    prevAmountRef.current = amount;
    
    return () => {
      if (gainTimeoutRef.current) {
        clearTimeout(gainTimeoutRef.current);
      }
    };
  }, [amount]);
  
  // Determine rate color
  const getRateClass = () => {
    if (rate > 0) return 'positive';
    if (rate < 0) return 'negative';
    return 'zero';
  };

  return (
    <div 
      className={`rp-resource-row ${isExpanded ? 'expanded' : ''} ${isZeroProduction ? 'zero-production' : ''}`}
      onClick={onClick}
    >
      {/* Main Row */}
      <div className="rp-row-main">
        <span className={`rp-row-icon ${isGaining ? 'pulse' : ''}`}>{resource.icon}</span>
        <span className="rp-row-name">{resource.name}</span>
        <span className={`rp-row-amount ${isGaining ? 'flash' : ''}`}>
          {formatNumber(animatedAmount)}
        </span>
        {/* Floating gains container */}
        <div className="rp-floating-gains-container">
          {floatingGains.map(gain => (
            <span 
              key={gain.id} 
              className={`rp-floating-gain ${gain.sizeClass}`}
              style={{
                left: `${gain.x}px`,
              }}
            >
              {formatGain(gain.value)}
            </span>
          ))}
        </div>
        {rate !== 0 && (
          <span className={`rp-row-rate ${getRateClass()}`}>
            {formatRate(rate)}/s
          </span>
        )}
        {hasModifiers && (
          <span 
            className="rp-row-modifier-badge"
            title={getModifierTooltip(breakdown)}
          >
            ✨
          </span>
        )}
      </div>
      
      {/* Milestone Progress Bar (always visible, subtle) */}
      <div 
        className="rp-row-progress"
        title={`${formatNumber(amount)} / ${formatNumberCompact(nextMilestone)} (${Math.round(milestoneProgress)}%)`}
      >
        <div 
          className={`rp-row-progress-fill ${isGaining ? 'glow' : ''}`}
          style={{ width: `${milestoneProgress}%` }}
        />
      </div>
      
      {/* Expanded Details */}
      {isExpanded && (
        <div className="rp-row-details">
          <div className="rp-row-detail-header">
            <span className="rp-detail-exact">{Math.floor(amount).toLocaleString()}</span>
            <span className="rp-detail-milestone">→ {formatNumberCompact(nextMilestone)}</span>
          </div>
          
          <p className="rp-row-desc">{resource.description}</p>
          
          {breakdown && (
            <div className="rp-row-breakdown">
              {/* Base production */}
              {breakdown.base > 0 && (
                <div className="rp-bd-row base">
                  <span className="rp-bd-label">🏠 Buildings</span>
                  <span className="rp-bd-value">+{formatRateDetailed(breakdown.base)}/s</span>
                </div>
              )}
              
              {/* Building details */}
              {breakdown.buildings && breakdown.buildings.length > 0 && (
                <div className="rp-bd-buildings">
                  {breakdown.buildings.slice(0, 4).map((b, i) => (
                    <div key={i} className="rp-bd-building">
                      <span>{b.name} ×{b.count}</span>
                      <span>+{formatRateDetailed(b.totalRate)}</span>
                    </div>
                  ))}
                  {breakdown.buildings.length > 4 && (
                    <div className="rp-bd-building more">
                      +{breakdown.buildings.length - 4} more...
                    </div>
                  )}
                </div>
              )}
              
              {/* Advisor bonus */}
              {breakdown.advisorBonus !== 0 && (
                <div className={`rp-bd-row ${breakdown.advisorBonus > 0 ? 'bonus' : 'penalty'}`}>
                  <span className="rp-bd-label">👑 Advisors</span>
                  <span className="rp-bd-value">
                    {breakdown.advisorBonus > 0 ? '+' : ''}{Math.round(breakdown.advisorBonus * 100)}%
                  </span>
                </div>
              )}
              
              {/* Advisor penalty */}
              {breakdown.advisorPenalty !== 0 && (
                <div className="rp-bd-row penalty">
                  <span className="rp-bd-label">👑 Penalty</span>
                  <span className="rp-bd-value">
                    {Math.round(breakdown.advisorPenalty * 100)}%
                  </span>
                </div>
              )}
              
              {/* Wonder bonus */}
              {breakdown.wonderBonus !== 0 && (
                <div className="rp-bd-row bonus">
                  <span className="rp-bd-label">🏛️ Wonders</span>
                  <span className="rp-bd-value">
                    +{Math.round(breakdown.wonderBonus * 100)}%
                  </span>
                </div>
              )}
              
              {/* Divider and total */}
              {(breakdown.advisorBonus !== 0 || breakdown.advisorPenalty !== 0 ||
                breakdown.wonderBonus !== 0) && (
                <>
                  <div className="rp-bd-divider" />
                  <div className="rp-bd-row total">
                    <span className="rp-bd-label">Total</span>
                    <span className="rp-bd-value">+{formatRateDetailed(rate)}/s</span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// DUNGEON RESOURCE ROW COMPONENT (simplified, no production)
// ============================================================
function DungeonResourceRow({
  resourceId,
  resource,
  amount,
  isExpanded,
  onClick,
}) {
  // Stack size progress
  const stackProgress = (amount / resource.stackSize) * 100;

  // Rarity color mapping
  const rarityColors = {
    common: '#9ca3af',     // Gray
    uncommon: '#10b981',   // Green
    rare: '#3b82f6',       // Blue
    epic: '#a855f7',       // Purple
    legendary: '#f59e0b',  // Amber
  };

  const rarityColor = rarityColors[resource.rarity] || '#9ca3af';

  return (
    <div
      className={`rp-resource-row dungeon-resource ${isExpanded ? 'expanded' : ''}`}
      onClick={onClick}
    >
      {/* Main Row */}
      <div className="rp-row-main">
        <span className="rp-row-icon" style={{ filter: `drop-shadow(0 0 2px ${rarityColor})` }}>
          {resource.icon}
        </span>
        <span className="rp-row-name" style={{ color: rarityColor }}>
          {resource.name}
        </span>
        <span className="rp-row-amount">
          {formatNumber(amount)}
        </span>
        <span className="rp-row-stack-size" style={{ color: rarityColor }}>
          /{formatNumberCompact(resource.stackSize)}
        </span>
      </div>

      {/* Stack Progress Bar */}
      <div
        className="rp-row-progress"
        title={`${formatNumber(amount)} / ${formatNumberCompact(resource.stackSize)} (${Math.round(stackProgress)}%)`}
      >
        <div
          className="rp-row-progress-fill"
          style={{
            width: `${Math.min(100, stackProgress)}%`,
            backgroundColor: rarityColor,
          }}
        />
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="rp-row-details">
          <div className="rp-row-detail-header">
            <span className="rp-detail-exact">{Math.floor(amount).toLocaleString()}</span>
            <span className="rp-detail-rarity" style={{ color: rarityColor }}>
              {resource.rarity.toUpperCase()}
            </span>
          </div>

          <p className="rp-row-desc">{resource.description}</p>
        </div>
      )}
    </div>
  );
}

export default ResourcePanel;
